import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, apiWithAuth } from '../../lib/api';
import {
  ArrowLeftIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { HandThumbUpIcon as HandThumbUpIconSolid } from '@heroicons/react/24/solid';

const ReviewsPenalties = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('reviews'); // 'reviews' or 'penalties'
  const [stats, setStats] = useState({ good: 0, bad: 0, good_rate: 100 });
  const [penalty, setPenalty] = useState({ points: 0, status: 'healthy', history: [] });
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const authApi = apiWithAuth(token); // Ensure apiWithAuth uses the token

      // 1. Get Me (to get ID)
      const meRes = await authApi.get('/auth/me');
      const myId = meRes.data.id;

      // 2. Get Stats
      const statRes = await api.get(`/reviews/stats/${myId}`);
      setStats(statRes.data);

      // 3. Get Reviews
      const revRes = await api.get(`/reviews/user/${myId}`);
      setReviews(revRes.data);

      // 4. Get Penalties
      const penRes = await authApi.get('/user/penalties');
      setPenalty(penRes.data);

    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-gray-200 sticky top-0 z-10 flex items-center">
        <button onClick={() => navigate(-1)} className="mr-4 text-gray-600">
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">レビュー・ペナルティ</h1>
      </div>

      {/* Tabs */}
      <div className="flex border-b bg-white">
        <button
          className={`flex-1 py-3 font-bold ${activeTab === 'reviews' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('reviews')}
        >
          レビュー ({stats.good + stats.bad})
        </button>
        <button
          className={`flex-1 py-3 font-bold ${activeTab === 'penalties' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('penalties')}
        >
          ペナルティ ({penalty.points}pt)
        </button>
      </div>

      <div className="p-4">
        {activeTab === 'reviews' ? (
          <>
            {/* Good Rate Card */}
            <div className="bg-white rounded-lg shadow p-6 mb-4 text-center">
              <div className="text-gray-500 mb-1">Good率</div>
              <div className="text-4xl font-bold text-blue-600 mb-2">{stats.good_rate}%</div>
              <div className="flex justify-center space-x-8">
                <div className="flex items-center text-gray-600">
                  <HandThumbUpIconSolid className="w-5 h-5 text-blue-500 mr-2" />
                  <span>{stats.good}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <HandThumbDownIcon className="w-5 h-5 text-gray-400 mr-2" />
                  <span>{stats.bad}</span>
                </div>
              </div>
            </div>

            {/* Review List */}
            <h2 className="font-bold text-gray-700 mb-3">レビュー履歴</h2>
            {reviews.length === 0 ? (
              <p className="text-center text-gray-400 py-4">まだレビューがありません</p>
            ) : (
              reviews.map(rev => (
                <div key={rev.id} className="bg-white rounded-lg shadow p-4 mb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-200 rounded-full mr-3 overflow-hidden">
                        {rev.reviewer_avatar && <img src={rev.reviewer_avatar} className="w-full h-full object-cover" />}
                      </div>
                      <div>
                        <div className="font-bold">{rev.reviewer_name}</div>
                        <div className="text-xs text-gray-500">{new Date(rev.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                    {rev.rating_type === 'good' ? (
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded font-bold">GOOD</span>
                    ) : (
                      <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded font-bold">BAD</span>
                    )}
                  </div>
                  {rev.comment && (
                    <div className="mt-3 text-sm text-gray-700 bg-gray-50 p-2 rounded">
                      {rev.comment}
                    </div>
                  )}
                  {rev.tags && rev.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {rev.tags.map(tag => (
                        <span key={tag} className="text-xs border border-gray-200 px-1 rounded text-gray-500">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </>
        ) : (
          <>
            {/* Penalty Gauge */}
            <div className="bg-white rounded-lg shadow p-6 mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-gray-800">現在のペナルティポイント</h3>
                <span className={`text-2xl font-bold ${penalty.points > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {penalty.points} pt
                </span>
              </div>

              {/* Visual Bar */}
              <div className="w-full bg-gray-200 rounded-full h-4 mb-2 relative overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${penalty.points >= 7 ? 'bg-red-600' : 'bg-yellow-400'}`}
                  style={{ width: `${Math.min(penalty.points / 8 * 100, 100)}%` }}
                ></div>
                {/* Threshold Markers */}
                <div className="absolute top-0 bottom-0 left-[87.5%] w-0.5 bg-white opacity-50"></div> {/* 7pt */}
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>0 (安全)</span>
                <span>4 (利用制限)</span>
                <span>8 (永久停止)</span>
              </div>

              {penalty.status !== 'healthy' && (
                <div className="mt-4 bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-start">
                  <ExclamationTriangleIcon className="w-5 h-5 mr-2 flex-shrink-0" />
                  <div>
                    <p className="font-bold mb-1">
                      {penalty.status === 'suspended' ? 'アカウント利用停止中' :
                        penalty.status === 'restricted' ? '一部機能制限中' : '注意'}
                    </p>
                    <p>
                      直前キャンセルなどによりポイントが蓄積しています。
                      ポイントは利用実績に応じて徐々に減少します。
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* History */}
            <h2 className="font-bold text-gray-700 mb-3">ペナルティ履歴</h2>
            {penalty.history.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg shadow">
                <HandThumbUpIcon className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="font-bold text-gray-800">素晴らしい！</p>
                <p className="text-gray-500 text-sm">現在ペナルティはありません。</p>
              </div>
            ) : (
              penalty.history.map((item, idx) => (
                <div key={idx} className="bg-white rounded-lg shadow p-3 mb-2 flex justify-between items-center">
                  <div>
                    <div className="text-red-600 font-bold mb-1">+{item.points} pt</div>
                    <div className="text-sm text-gray-800">{item.reason}</div>
                    <div className="text-xs text-gray-400">{new Date(item.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReviewsPenalties;